// Renewal "next best action" (Oct 2026): for every renewal or policy anniversary in the coming weeks, the ONE thing
// most worth doing now, with the reason in plain words and the other things worth knowing. Computed only from the
// advisor's own data, explained, never automatic: the advisor turns an action into a task with one click.
//
// Order of importance (the first that applies is the action; the others are listed as "also"):
//   1 settle_arrears     an unpaid premium on a policy that renews soon — the cover itself is at risk
//   2 call_detractor     the client scored the service 0–6 in the last 12 months — talk before any renewal offer
//   3 resolve_service    an open claim or service request — a renewal conversation lands badly while it is open
//   4 general renewals, by stage: request_terms (not started) · send_terms (terms compared) · follow_up_terms (sent and
//     waiting ≥ 5 days) · confirm_renewal (approved — record «Renewed») · explain_increase (renewal premium ≥ 15 % up)
//   5 check_sum_insured  a linked property with no rebuild value or a value below area × cost per m²
//   6 renew_kyc          (life) the client's KYC file is incomplete or due for review
//   7 review_cover       (life) no review in the last 12 months before the anniversary
//   8 confirm_contact    no contact recorded in the last 6 months — call before the renewal
import { stableId } from './ids.ts';
import { DomainError, created, put, type Ctx } from './cmd.ts';
import type { Changeset, WorkspaceView } from './changeset.ts';
import { addDays, diffDays, type Day } from './dates.ts';
import { isActivePolicy, nextPolicyAnniversary } from './rules.ts';
import { generalRenewalPipeline, isGeneralPolicy } from './gi-renewals.ts';
import { openArrears } from './premiums.ts';
import { kycNeeded, kycStatus } from './kyc.ts';
import { rebuildCostPerSqm, underInsuranceHint } from './insured.ts';
import { annualNow } from './policy-engine.ts';
import type { GiRenewalStage, Policy } from './schema.ts';

export const NEXT_ACTION_KINDS = [
  'settle_arrears', 'call_detractor', 'resolve_service', 'request_terms', 'send_terms', 'follow_up_terms', 'confirm_renewal',
  'explain_increase', 'check_sum_insured', 'renew_kyc', 'review_cover', 'confirm_contact', 'prepare_renewal',
] as const;
export type NextActionKind = typeof NEXT_ACTION_KINDS[number];
const SCORE: Record<NextActionKind, number> = {
  settle_arrears: 90, call_detractor: 84, resolve_service: 78, confirm_renewal: 74, follow_up_terms: 70, send_terms: 66, request_terms: 62,
  explain_increase: 58, check_sum_insured: 52, renew_kyc: 50, review_cover: 46, confirm_contact: 42, prepare_renewal: 30,
};
export interface NextAction {
  id: string; policy: Policy; personId: string; dueDate: Day; days: number; line: 'general' | 'life';
  kind: NextActionKind; params: Record<string, string | number>;
  /** the other signals on the same renewal, least urgent last */
  also: NextActionKind[];
  score: number; stage: GiRenewalStage | null;
  /** the task this action already became (if any) */
  taskId: string;
  taskExists: boolean;
}

export const nextActionTaskId = (policyId: string, dueDate: Day, kind: NextActionKind) => stableId('next_action_task', `${policyId}:${dueDate}:${kind}`);

/** next best actions for renewals within `horizon` days (general renewals as in the pipeline; life anniversaries ≤ horizon) */
export function renewalNextActions(v: WorkspaceView, today: Day, opts: { horizon?: number; limit?: number; personId?: string } = {}): NextAction[] {
  const horizon = opts.horizon ?? 60;
  const items: { policy: Policy; dueDate: Day; days: number; line: 'general' | 'life'; stage: GiRenewalStage | null; renewalPremium: number | null; lastPremium: number | null; stageAt: string | null }[] = [];
  for (const it of generalRenewalPipeline(v, today, { horizon })) items.push({ policy: it.policy, dueDate: it.dueDate, days: it.days, line: 'general', stage: it.stage, renewalPremium: it.renewalPremium, lastPremium: it.lastPremium, stageAt: it.renewal?.stageAt ?? null });
  for (const p of v.rows('policies')) {
    if (!isActivePolicy(p) || isGeneralPolicy(p)) continue;
    const person = v.get('persons', p.personId);
    if (!person || person.archivedAt) continue;
    const due = nextPolicyAnniversary(p, today);
    if (!due) continue;
    const days = diffDays(today, due);
    if (days < 0 || days > horizon) continue;
    items.push({ policy: p, dueDate: due, days, line: 'life', stage: null, renewalPremium: null, lastPremium: annualNow(v, p, today) || null, stageAt: null });
  }
  const yearAgo = addDays(today, -365), halfYear = addDays(today, -183);
  const out: NextAction[] = [];
  for (const it of items) {
    if (opts.personId && it.policy.personId !== opts.personId) continue;
    const pid = it.policy.personId;
    const found: { kind: NextActionKind; params: Record<string, string | number> }[] = [];
    const arrears = openArrears(v, today, { policyId: it.policy.id });
    if (arrears.length) found.push({ kind: 'settle_arrears', params: { amount: Math.round(arrears.reduce((s, a) => s + a.arrear.amount, 0) * 100) / 100, days: Math.max(...arrears.map((a) => a.days)) } });
    const detractor = v.where('nps_surveys', 'personId', pid).filter((s) => s.status === 'answered' && s.category === 'detractor' && (s.answeredAt ?? '').slice(0, 10) >= yearAgo).sort((a, b) => (b.answeredAt ?? '').localeCompare(a.answeredAt ?? ''))[0];
    if (detractor) found.push({ kind: 'call_detractor', params: { score: detractor.score ?? 0 } });
    const openService = v.where('service_cases', 'personId', pid).filter((s) => s.status !== 'closed').length + v.where('claims', 'personId', pid).filter((c) => !['paid', 'declined', 'closed'].includes(c.status)).length;
    if (openService) found.push({ kind: 'resolve_service', params: { count: openService } });
    if (it.line === 'general') {
      const waited = it.stageAt ? diffDays(it.stageAt.slice(0, 10), today) : 0;
      if (it.stage === 'approved') found.push({ kind: 'confirm_renewal', params: {} });
      else if (it.stage === 'sent_to_client' && waited >= 5) found.push({ kind: 'follow_up_terms', params: { days: waited } });
      else if (it.stage === 'compared') found.push({ kind: 'send_terms', params: {} });
      else if (!it.stage && it.days <= 45) found.push({ kind: 'request_terms', params: {} });
      if (it.renewalPremium != null && it.lastPremium && it.renewalPremium >= it.lastPremium * 1.15) found.push({ kind: 'explain_increase', params: { pct: Math.round((it.renewalPremium / it.lastPremium - 1) * 100) } });
      const cost = rebuildCostPerSqm(v);
      const weak = v.where('insured_items', 'personId', pid).find((i) => i.status === 'active' && (i.policyIds ?? []).includes(it.policy.id) && (() => { const h = underInsuranceHint(i, cost); return !!h; })());
      if (weak) found.push({ kind: 'check_sum_insured', params: { item: weak.label } });
    } else {
      if (kycNeeded(v, pid)) { const k = kycStatus(v, pid, today); if (k.status !== 'complete' || (k.daysToReview != null && k.daysToReview <= 30)) found.push({ kind: 'renew_kyc', params: {} }); }
      const person = v.get('persons', pid)!;
      const lastReview = [person.lastReviewDate, ...v.where('reviews', 'personId', pid).map((r) => r.date)].filter((d): d is string => !!d).sort().pop() ?? null;
      if (!lastReview || lastReview < yearAgo) found.push({ kind: 'review_cover', params: lastReview ? { date: lastReview } : {} });
    }
    const lastContact = [
      ...v.where('communications', 'personId', pid).map((c) => c.at),
      ...v.where('meetings', 'personId', pid).filter((m) => m.status === 'held').map((m) => m.at),
      ...v.where('timeline', 'personId', pid).filter((e) => e.type === 'call' || e.type === 'meeting' || e.type === 'message' || e.type === 'email').map((e) => e.at),
    ].map((d) => String(d ?? '').slice(0, 10)).filter((d) => d && d <= today).sort().pop() ?? null;
    if (!lastContact || lastContact < halfYear) found.push({ kind: 'confirm_contact', params: lastContact ? { date: lastContact } : {} });
    if (!found.length) found.push({ kind: 'prepare_renewal', params: {} });
    found.sort((a, b) => SCORE[b.kind] - SCORE[a.kind]);
    const top = found[0]!;
    const urgency = Math.max(0, 30 - Math.max(0, it.days)) / 3; // up to +10 inside the last month
    const taskId = nextActionTaskId(it.policy.id, it.dueDate, top.kind);
    out.push({
      id: `${it.policy.id}:${it.dueDate}`, policy: it.policy, personId: pid, dueDate: it.dueDate, days: it.days, line: it.line, stage: it.stage,
      kind: top.kind, params: top.params, also: found.slice(1).map((f) => f.kind), score: Math.round(SCORE[top.kind] + urgency), taskId, taskExists: !!v.get('tasks', taskId),
    });
  }
  out.sort((a, b) => b.score - a.score || a.days - b.days);
  return opts.limit ? out.slice(0, opts.limit) : out;
}

/** the action as a task (deterministic id: two clicks or two devices never make two tasks) */
export function createNextActionTask(v: WorkspaceView, a: Pick<NextAction, 'policy' | 'dueDate' | 'kind' | 'days'>, title: string, ctx: Ctx): Changeset {
  const id = nextActionTaskId(a.policy.id, a.dueDate, a.kind);
  if (v.get('tasks', id)) throw new DomainError('next_action.task_exists');
  if (!title.trim()) throw new DomainError('task.title_required');
  const urgent = a.kind === 'settle_arrears' || a.kind === 'call_detractor' || a.days <= 15;
  const task = created<'tasks'>(ctx, {
    id, kind: a.kind === 'review_cover' ? 'review' : a.kind === 'call_detractor' || a.kind === 'confirm_contact' ? 'call' : 'renewal', title: title.trim(),
    personId: a.policy.personId, prospectId: null, caseId: null, policyId: a.policy.id, dueDate: ctx.today, dueTime: null, durationMin: null,
    locationType: null, location: null, status: 'open', priority: urgent ? 'high' : 'normal', notes: null, origin: 'next_action',
    data: { kind: a.kind, dueDate: a.dueDate },
  });
  return { action: 'next_action.task', entity: 'tasks', entityId: id, summary: title, puts: [put('tasks', task)], deletes: [] };
}
